// Ordered for the legend (core first, then the four source hubs).
export const HEARTBEAT_CLUSTERS = [
    { key: "self", label: "You", source: "persona" },
    { key: "body", label: "Body", source: "wearable" },
    { key: "context", label: "Context", source: "calendar" },
    { key: "world", label: "Environment", source: "ambient" },
    { key: "care", label: "Care", source: "meds" },
];
// Exhaustive over SignalKind: adding a kind without a visual is a type error, so
// the fingerprint can never silently drop a signal it is actually tracking.
const SIGNAL_VISUALS = {
    "wearable.sleep": { cluster: "body", label: "Sleep", icon: "moon" },
    "wearable.nap": { cluster: "body", label: "Nap", icon: "moon" },
    "wearable.hrv": { cluster: "body", label: "Overnight HRV", icon: "pulse" },
    "wearable.rhr": { cluster: "body", label: "Resting HR", icon: "heartbeat" },
    "wearable.stress": { cluster: "body", label: "Stress", icon: "wind" },
    "wearable.activity": { cluster: "body", label: "Movement", icon: "person-simple-walk" },
    "wearable.workout": { cluster: "body", label: "Workout", icon: "barbell" },
    "wearable.bp.systolic": { cluster: "body", label: "Blood pressure", icon: "gauge" },
    "wearable.bp.diastolic": { cluster: "body", label: "Blood pressure", icon: "gauge" },
    "wearable.weight": { cluster: "body", label: "Weight", icon: "scales" },
    "wearable.glucose": { cluster: "body", label: "Glucose", icon: "test-tube" },
    "wearable.spo2": { cluster: "body", label: "Blood oxygen", icon: "drop-half" },
    "wearable.respiration": { cluster: "body", label: "Respiration", icon: "wind" },
    "wearable.skin_temp": { cluster: "body", label: "Skin temp", icon: "thermometer" },
    "wearable.vo2max": { cluster: "body", label: "VO2 max", icon: "gauge" },
    "self_report.weight": { cluster: "body", label: "Weight", icon: "scales" },
    "self_report.glp1_dose": { cluster: "care", label: "GLP-1 dose", icon: "syringe" },
    "self_report.checkin": { cluster: "context", label: "Check-in", icon: "smiley" },
    "self_report.confounder": { cluster: "context", label: "Out of my control", icon: "hand-palm" },
    "connection.log": { cluster: "context", label: "Connection", icon: "handshake" },
    "nutrition.meal": { cluster: "care", label: "Meals", icon: "fork-knife" },
    "nutrition.sodium_mg": { cluster: "care", label: "Sodium", icon: "fork-knife" },
    "hydration.ml": { cluster: "care", label: "Hydration", icon: "drop" },
    "medication.dose": { cluster: "care", label: "Medication", icon: "pill" },
    "calendar.event": { cluster: "context", label: "Calendar", icon: "calendar-blank" },
    "user.desk_session": { cluster: "context", label: "At the desk", icon: "desktop" },
    "user.app_open": { cluster: "context", label: "App activity", icon: "device-mobile" },
    "user.completion_moment": { cluster: "context", label: "App activity", icon: "clock" },
    "user.notification_dismissed": { cluster: "context", label: "Notifications", icon: "bell-slash" },
    "user.microstep_added": { cluster: "context", label: "Moment", icon: "plus-circle" },
    "user.microstep_adjusted": { cluster: "context", label: "Moment", icon: "sliders" },
    "user.microstep_completed": { cluster: "context", label: "Moment done", icon: "check-circle" },
    "user.reset_watched": { cluster: "context", label: "Reset watched", icon: "play-circle" },
    "user.microstep_feedback": { cluster: "context", label: "Feedback", icon: "chat-circle" },
    "user.microstep_verdict": { cluster: "context", label: "Verdict", icon: "chat-circle" },
    "user.feedback": { cluster: "context", label: "Feedback", icon: "chat-circle" },
    "adherence.slippage_detected": { cluster: "context", label: "Adherence", icon: "trend-down" },
    "adherence.recovered": { cluster: "context", label: "Adherence", icon: "trend-up" },
    "environment.weather_today": { cluster: "world", label: "Weather", icon: "cloud-sun" },
    "environment.weather_archive": { cluster: "world", label: "Weather history", icon: "cloud-sun" },
    "environment.location_bucket": { cluster: "world", label: "Location", icon: "map-pin" },
    "environment.air_quality": { cluster: "world", label: "Air quality", icon: "wind" },
    "environment.pollen": { cluster: "world", label: "Pollen", icon: "flower" },
    "environment.uv": { cluster: "world", label: "UV index", icon: "sun" },
    "environment.solar": { cluster: "world", label: "Daylight", icon: "sun-horizon" },
    "survey.pulse": { cluster: "body", label: "Wellbeing check-in", icon: "heart" },
    "lab.hba1c": { cluster: "care", label: "HbA1c", icon: "test-tube" },
    "external.company_event": { cluster: "world", label: "Work context", icon: "buildings" },
};
const FALLBACK_VISUAL = { cluster: "context", label: "Signal", icon: "circle" };
// Resolve a signal kind to its fingerprint visual. Falls back gracefully for an
// unrecognised kind (e.g. a newly added kind not yet mapped) rather than
// throwing into a read path.
export function signalVisual(kind) {
    return SIGNAL_VISUALS[kind] ?? FALLBACK_VISUAL;
}
export const SIGNAL_RELATIONSHIPS = [
    // Sleep and overnight recovery
    { a: "wearable.sleep", b: "wearable.hrv", label: "recovery" },
    { a: "wearable.sleep", b: "wearable.rhr", label: "recovery" },
    { a: "wearable.sleep", b: "wearable.respiration", label: "recovery" },
    { a: "wearable.nap", b: "wearable.sleep", label: "sleep debt" },
    // Circadian: light drives sleep timing
    { a: "wearable.sleep", b: "environment.solar", label: "circadian" },
    { a: "wearable.sleep", b: "environment.uv", label: "circadian" },
    // Training load and cardiovascular fitness
    { a: "wearable.activity", b: "wearable.rhr", label: "fitness" },
    { a: "wearable.activity", b: "wearable.sleep", label: "recovery" },
    { a: "wearable.workout", b: "wearable.rhr", label: "fitness" },
    { a: "wearable.workout", b: "wearable.hrv", label: "recovery" },
    { a: "wearable.vo2max", b: "wearable.activity", label: "fitness" },
    // Autonomic stress load
    { a: "wearable.stress", b: "wearable.hrv", label: "stress load" },
    { a: "wearable.stress", b: "wearable.rhr", label: "stress load" },
    // Respiratory: air quality and pollen affect breathing + oxygenation
    { a: "wearable.respiration", b: "environment.air_quality", label: "respiratory" },
    { a: "wearable.respiration", b: "environment.pollen", label: "respiratory" },
    { a: "wearable.spo2", b: "environment.air_quality", label: "respiratory" },
    { a: "wearable.spo2", b: "wearable.respiration", label: "respiratory" },
    // Metabolic and body composition
    { a: "nutrition.meal", b: "wearable.glucose", label: "metabolic" },
    { a: "wearable.activity", b: "wearable.glucose", label: "metabolic" },
    { a: "wearable.weight", b: "wearable.activity", label: "energy balance" },
    // GLP-1 care loop
    { a: "self_report.glp1_dose", b: "wearable.weight", label: "medication" },
    { a: "self_report.glp1_dose", b: "nutrition.meal", label: "appetite" },
    { a: "medication.dose", b: "wearable.bp.systolic", label: "medication" },
    // Calendar load shows up in overnight recovery
    { a: "calendar.event", b: "wearable.hrv", label: "load" },
    { a: "calendar.event", b: "wearable.stress", label: "load" },
];
export function signalLabel(kind) {
    return signalVisual(kind).label;
}
//# sourceMappingURL=signal-visual.js.map